import { Controller } from "@hotwired/stimulus"
import { Loggable } from "./concerns/loggable"
import { ShowHide } from "./concerns/showHide"
import { VariousHelpers } from "./concerns/various_helpers"
import { HasIntervalOutlets } from "./concerns/timer_player/has_interval_outlets"
import NoSleep from "@scottjgilroy/no-sleep" // es un fork, de un fork de nosleep.js con fix para PWA en iOS
import { Howl } from "howler"
import { usePwaHelpers } from "./concerns/use_pwa_helpers";

export default class extends Controller {

  static outlets = [
    "breathing-square",
    "navbar",
    // los distintos tipos de inteval tienen distintos identifiers
    "basic-interval",
    "loop-interval",
    "breathwork-interval",
    "asana-interval"
  ]

  static targets = [
    "timerName", "intervalName",
    "toggleButton", "restartButton",
    "finishEarlyButton",
    "editor",
    "durationMessage", "totalElapsedTimeMessage", "elapsedTimeMessage", "clickMessage",
    "actionMessage", "nextActionMessage",
    "soundHelp",
    "volumeSelect",
    "clickAudio", "phaseChangeAudio", "metronomeChangeAudio", "endAudio", "startAudio",
    "progressBar", "presentation", "sideMessage", "countTotal", "countLabel",
    "elapsedLabel", "cycleMessage", "liveMessage", "nextPreview", "intervalProgress"
  ]

  static values = {
    state: String,
    name: String,
    strLoading: String,
    strStart: String,
    strStop: String,
    strContinue: String,
    strFinished: String,
    strNext: String,
    volume: Number,
    labels: Object
  }

  setVolume(){
    if (this.hasVolumeSelectTarget) {
      this.volumeValue = this.volumeSelectTarget.value/ 100
      this.preloadAudios() // audios are loaded with the volume value
      this.playAudio("click")
    }
  }

  initialize() {
    Loggable(this, {debug: false})
    ShowHide(this, {hiddenClassName: "d-none"})
    VariousHelpers(this)
    HasIntervalOutlets(this)
    usePwaHelpers(this)
  }

  connect() {
    this.progressMotionPreference = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    this.progressMotionChanged = () => this.cancelProgressAnimation()
    this.progressMotionPreference?.addEventListener("change", this.progressMotionChanged)
    this.setState("loading")

    this.documentListeners = []
    this.setupBrowser()

    this.highlightClassName = "list-group-item-secondary"

    if (this.hasVolumeSelectTarget) {
      this.volumeValue = this.volumeSelectTarget.value/ 100
    }

    this.noSleep = new NoSleep()
    this.worker = new Worker("/metronome-worker.js")

    this.intervalIndex = 0
    this.currentClick = 0
    this.listenOutlets()

    this.setState("stopped")

    // no muestro el botón de play si no hay intervals.
    if (this.loadIntervals().length === 0) {
      this.hide(this.toggleButtonTarget)
      if (this.hasPresentationTarget) {
        this.actionMessageTarget.innerText = this.labelsValue.empty
        this.announce(this.labelsValue.empty)
      }
    }
  }

  disconnect() {
    this.resetPresentation()
    this.progressMotionPreference?.removeEventListener("change", this.progressMotionChanged)
    this.stateValue = "stopped"
    this.stopPlayback()
    this.documentListeners.forEach(([name, listener]) => document.removeEventListener(name, listener))
    this.documentListeners = []
    if (this.worker) {
      this.worker.terminate()
    }
    this.unloadAudios()
    this.deSetupBrowser()
  }

  togglePlay() {
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.preloadAudios()
      // es importante que este directo acá en la función y no dentro de otra función, para que browser vea que es user-triggerd y habilite sonido.
      this.soundEnabler = new Howl({src: [this.clickAudioTarget.src]})
      this.soundEnabler.play()
      this.preStart()
    } else if (this.stateValue === "playing") {
      this.stop()
    }
  }

  togglePlayWithKey(event) {
    if (this.keyboardEventTargetIsEditable(event)) {
      return
    }

    event.preventDefault()
    this.togglePlay()
  }

  skipToPreviousIntervalWithKey(event) {
    this.skipToRelativeIntervalWithKey(event, -1)
  }

  skipToNextIntervalWithKey(event) {
    this.skipToRelativeIntervalWithKey(event, 1)
  }

  skipToRelativeIntervalWithKey(event, offset) {
    if (this.keyboardEventTargetIsEditable(event) || this.stateValue !== "playing") {
      return
    }

    event.preventDefault()

    let index = this.intervalIndex + offset
    if (index < 0 || index >= this.loadIntervals().length) {
      return
    }

    this.startMetronome(index)
  }

  keyboardEventTargetIsEditable(event) {
    if (!event.target) {
      return false
    }

    return ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName) || event.target.isContentEditable
  }

  preStart() {
    clearTimeout(this.countdownTimeout)
    this.setState("pre-playing")

    this.clearHighlighCurrentInterval()
    this.clearPlayerMessages()
    this.clickMessageTarget.innerText = `...`
    // this.playAudio("click") - acaba de sonar el del togglePlay()
    this.countdownTimeout = setTimeout(() => {
      this.clickMessageTarget.innerText = `..`
      this.playAudio("click")
      this.countdownTimeout = setTimeout(() => {
        this.clickMessageTarget.innerText = `.`
        this.playAudio("click")
        this.startOrContinue()
      }, 800)
    }, 800)
  }

  startOrContinue(){
    this.log("startOrContinue")
    if (this.currentClick > 0) {
      this.continue()
    } else {
      this.start()
    }
  }

  start() {
    this.loadIntervals()
    this.setState("playing")

    // must be in user-triggered function
    this.noSleep.enable()

    this.startTickWorker()

    this.currentInterval().start()
  }

  continue(){
    this.setState("playing")

    // must be in user-triggered function
    this.noSleep.enable()

    this.startTickWorker()

    this.currentInterval().continue()
  }

  startTickWorker(){
    this.worker.postMessage('start');
    this.worker.onmessage = (e) => {
      if (e.data === 'tick') {
        this.click()
      }
    };
  }

  stopPlayback() {
    if (this.hasBreathingSquareOutlet) this.breathingSquareOutlet.pause()
    this.progressAnimation?.pause()
    clearTimeout(this.countdownTimeout)
    this.noSleep.disable()
    this.worker.onmessage = null
    this.worker.postMessage('stop');
  }

  stop() {
    this.stopPlayback()
    this.setState("stopped")
    if (this.currentInterval()) {
      this.currentInterval().stop()
    }
  }

  restart(){
    this.stop()
    this.intervalIndex = 0
    this.currentClick = 0
    this.currentInterval().initClicks()
    this.preStart()
  }

  finishEarly() {
    if (this.stateValue !== "stopped" || this.currentClick === 0) return
    if (window.confirm(this.labelsValue.finish_confirmation)) this.finish()
  }

  finish(){
    this.stop()
    this.playAudio("end")
    this.setState("finished")
    this.clearHighlighCurrentInterval()
  }

  click() {
    this.currentInterval().click()
    if (this.stateValue !== "finished") {
      this.render()
      this.currentClick++
    }
    document.dispatchEvent(new CustomEvent("timer:click:end"))
  }

  render(){
    this.currentInterval().renderIntervalName()
    this.highlightCurrentInterval()
    this.renderElapsedTime()
  }

  formatTime(seconds) {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
  }

  renderPresentation(data) {
    if (!this.hasPresentationTarget) return
    const labels = this.labelsValue
    let repetitionMessage = ""
    this.presentationTarget.dataset.kind = data.type
    this.sideMessageTarget.textContent = data.side || ""
    this.countTotalTarget.textContent = data.type === "basic" ? `/ ${this.formatTime(data.duration)}` : ""
    this.countLabelTarget.textContent = ""
    this.elapsedLabelTarget.textContent = ""
    this.cycleMessageTarget.textContent = ""
    this.nextPreviewTarget.hidden = !this.nextActionMessageTarget.innerText
    this.renderProgress(data)
    if (this.hasIntervalProgressTarget) {
      this.intervalProgressTarget.hidden = data.type !== "asana"
      if (data.type === "asana") {
        this.intervalProgressTarget.max = data.duration
        this.intervalProgressTarget.value = data.elapsed
      }
    }

    if (data.type === "loop") {
      const [label, count] = labels.repetition.split("%{current}")
      repetitionMessage = labels.repetition.replace("%{current}", data.cycle).replace("%{total}", data.cycles)
      this.clickMessageTarget.innerText = label.trim()
      this.countTotalTarget.textContent = `${data.cycle}${count.replace("%{total}", data.cycles)}`.trim()
      this.elapsedTimeMessageTarget.innerText = `${this.formatTime(data.elapsed)} / ${this.formatTime(data.duration)}`
    }
    if (data.type === "breathwork") {
      this.actionMessageTarget.innerText = data.action
      this.countTotalTarget.textContent = `/ ${data.durations[data.phase]}`
      this.cycleMessageTarget.textContent = labels.cycles.replace("%{current}", data.cycle).replace("%{total}", data.cycles)
      if (this.hasBreathingSquareOutlet) {
        this.breathingSquareOutlet.update({phase: data.phase, elapsed: data.phaseElapsed,
          durations: data.durations, playing: this.stateValue === "playing"})
      }
    }
    this.announce([data.name, data.action, data.side, repetitionMessage, this.stateValue === "stopped" ? labels.paused : ""].filter(Boolean).join(" · "))
  }

  announce(message) {
    if (this.hasLiveMessageTarget && this.liveMessageTarget.textContent !== message) {
      this.liveMessageTarget.textContent = message
    }
  }

  renderProgress({type, elapsed, duration}) {
    this.cancelProgressAnimation()
    const progress = Math.min(1, Math.max(0, elapsed / duration)) * 100
    this.presentationTarget.style.setProperty("--timer-interval-progress", `${progress}%`)
    if (!["basic", "loop", "asana"].includes(type) || this.stateValue !== "playing" ||
        this.progressMotionPreference?.matches || !window.CSS?.registerProperty ||
        !this.presentationTarget.animate || !(duration > 0)) return

    // The worker owns time. Only interpolate the visual portion of its latest pulse.
    const previous = Math.min(1, Math.max(0, (elapsed - 1) / duration)) * 100
    this.progressAnimation = this.presentationTarget.animate([
      {"--timer-interval-progress": `${previous}%`},
      {"--timer-interval-progress": `${progress}%`}
    ], {duration: 1000, easing: "linear", fill: "both"})
  }

  cancelProgressAnimation() {
    this.progressAnimation?.cancel()
    this.progressAnimation = null
  }

  resetPresentation() {
    this.cancelProgressAnimation()
    if (this.hasBreathingSquareOutlet) this.breathingSquareOutlet.reset()
    if (this.hasIntervalProgressTarget) {
      this.intervalProgressTarget.hidden = true
      this.intervalProgressTarget.value = 0
    }
    if (!this.hasPresentationTarget) return
    delete this.presentationTarget.dataset.kind
    this.presentationTarget.style.removeProperty("--timer-interval-progress")
    this.intervalNameTarget.innerText = ""
    const messages = [this.sideMessageTarget, this.countTotalTarget,
      this.countLabelTarget, this.elapsedLabelTarget, this.cycleMessageTarget]
    messages.forEach(element => { element.textContent = "" })
    this.nextPreviewTarget.hidden = true
  }

  clearHighlighCurrentInterval(){
    this.element.querySelectorAll(".playlist--interval-container").forEach((e) => {
      e.classList.remove(this.highlightClassName)
    })
  }
  highlightCurrentInterval(){
    let currentContainer = this.currentInterval().element.closest(".playlist--interval-container")
    this.element.querySelectorAll(".playlist--interval-container").forEach((e) => {
      if (e === currentContainer) {
        e.classList.add("list-group-item-secondary")
      } else {
        e.classList.remove(this.highlightClassName)
      }
    })
  }

  renderElapsedTime(){
    let humanClick = this.currentClick + 1
    let message = `${Math.floor(humanClick / 60)}:${(humanClick % 60).toString().padStart(2, '0')}`
    if (this._totalDuration) {
      message += ` / ${Math.floor(this._totalDuration / 60)}:${(this._totalDuration % 60).toString().padStart(2, '0')}`
    }
    this.totalElapsedTimeMessageTarget.innerText = message
  }

  hasAudio(name) {
    return this[`has${this.capitalize(name)}AudioTarget`]
  }

  preloadAudios(){
    this.unloadAudios()
    let names = ["click", "phaseChange", "metronomeChange", "end", "start"]
    this._preloadedAudios = {}
    names.forEach((name) => {
      if (this.hasAudio(name)) {
        this._preloadedAudios[name] = this.howlForAudioTarget(this[`${name}AudioTarget`])
      }
    })
  }

  unloadAudios(){
    if (this.soundEnabler) {
      this.soundEnabler.unload()
      this.soundEnabler = null
    }
    if (this._preloadedAudios) {
      Object.values(this._preloadedAudios).forEach((audio) => {
        audio.unload()
      })
      this._preloadedAudios = {}
    }
  }

  howlForAudioTarget(audioTarget){
    let sources
    if (audioTarget.src === "") {
      sources = [...audioTarget.querySelectorAll("source")].map((s) => { return s.src })
    } else {
      sources = [audioTarget.src]
    }
    let sound = new Howl({
      src: sources,
      volume: this.volumeValue,
      autosuspend: false,
      onload: () => {
        this.log("howler loaded")
      },
      onloaderror: (id, error) => {
        this.error("howler loaderror", error)
      },
      onplayerror: (id, error) => {
        this.error("howler playerror", error)
      }
    })
    return sound
  }

  playAudio(audioName){
    if (Howler.ctx.state === 'suspended' || Howler.ctx.state === 'interrupted') {
      // en teoría Howler lo hace solo pero hay edge cases en iOS.
      Howler.ctx.resume()
    }
    if (this.hasAudio(audioName)) {
      // Los sonidos fueron pre-cargados en preloadAudios()
      this._preloadedAudios[audioName].play()
    }
  }

  startNextMetronome() {
    this.clearPlayerMessages()
    if (this.onLastInterval()) {
      this.finish()
    } else {
      this.playAudio("metronomeChange")
      this.intervalIndex++
      this.currentInterval().start()
      this.currentClick-- // para compensar el click adicional
      this.click() // para que no quede 1s en el medio entre metronomos.
    }
  }

  // el elemento cuyo envento dispare este método debe tener un data-interval-index
  skipToInterval(event) {
    if (this.stateValue === "playing") {
      let index = parseInt(event.currentTarget.dataset.intervalIndex)
      this.log("skipToInterval", index)
      this.startMetronome(index)
    } else {
      this.log("skipToInterval: not playing")
    }
  }

  startMetronome(index) {
    this.clearPlayerMessages()
    this.currentInterval().stop()

    this.intervalIndex = index
    this.currentInterval().start()
    this.currentClick-- // para compensar el click adicional
    this.click() // para que no quede 1s en el medio entre metronomos.
  }

  setState(newValue) {
    this.stateValue = newValue
    this.showAll(this.editorTargets)
    this.toggleNavigation() // after setting stateValue
    switch (this.stateValue) {
      case "loading":
      break
      case "stopped":
        this.show(this.toggleButtonTarget)
        if (this.currentClick > 0) {
          this.toggleButtonTarget.innerText = this.strContinueValue
          this.showAll([this.restartButtonTarget,this.finishEarlyButtonTarget])
        } else {
          this.toggleButtonTarget.innerText = this.strStartValue
          this.hideAll([this.restartButtonTarget,this.finishEarlyButtonTarget])
        }
        this.toggleButtonTarget.disabled = false
        break
      case "finished":
        this.clearPlayerMessages()
        this.actionMessageTarget.innerText = this.strFinishedValue
        this.toggleButtonTarget.innerText = this.strStartValue
        this.hideAll([this.toggleButtonTarget,this.finishEarlyButtonTarget])
        this.show(this.restartButtonTarget)
        this.toggleButtonTarget.disabled = false
        document.dispatchEvent(new CustomEvent("timer:finished", {detail: {elapsedSeconds: this.currentClick}}))
        break
      case "pre-playing":
        this.hideAll(this.editorTargets)
        this.hide(this.toggleButtonTarget)
        this.hideAll([this.restartButtonTarget,this.finishEarlyButtonTarget])
        this.toggleButtonTarget.innerText = this.strStopValue
        this.toggleButtonTarget.disabled = false
        break
      case "playing":
        this.hideAll(this.editorTargets)
        this.show(this.toggleButtonTarget)
        this.toggleButtonTarget.innerText = this.strStopValue
        this.toggleButtonTarget.disabled = false
        break
      default:
        this.hideAll([this.restartButtonTarget,this.finishEarlyButtonTarget])
        this.log(`setState: unexpected state ${this.stateValue}`)
        this.toggleButtonTarget.innerText = "error"
        this.toggleButtonTarget.disabled = true
    }
    this.announceState()
  }

  announceState() {
    if (!this.hasPresentationTarget) return
    if (this.stateValue === "stopped") {
      const message = this.currentClick > 0 ? this.labelsValue.paused : this.labelsValue.ready
      if (this.currentClick === 0) this.actionMessageTarget.innerText = message
      this.announce(message)
    } else if (this.stateValue === "pre-playing") {
      this.announce(this.labelsValue.preparing)
    } else if (this.stateValue === "finished") {
      this.announce(this.strFinishedValue)
    }
  }

  listenToDocument(name, listener) {
    document.addEventListener(name, listener)
    this.documentListeners.push([name, listener])
  }

  listenOutlets() {
    this.listenToDocument("interval:durationChanged", (event) => {
      this.renderDuration()
    })
    this.listenToDocument("interval:stateChanged", (event) => {
      if (event.detail.state === "finished") {
        this.startNextMetronome()
      }
    })
  }

  calculateTotalDuration(intervals) {
    this._totalDuration = 0
    intervals.forEach((interval) => {
      this._totalDuration += interval._totalDuration
    })
  }

  renderDuration() {
    const intervals = this.loadIntervals()
    this.calculateTotalDuration(intervals)
    this.adjustSegmentsWidth(intervals)
    this.durationMessageTarget.innerText = `${Math.floor(this._totalDuration / 60)}:${(this._totalDuration % 60).toString().padStart(2, '0')}`
    if (this.hasPresentationTarget && this.currentClick === 0) {
      this.totalElapsedTimeMessageTarget.innerText = `0:00 / ${this.formatTime(this._totalDuration)}`
      if (intervals.length === 0) this.actionMessageTarget.innerText = this.labelsValue.empty
    }
  }

  adjustSegmentsWidth(intervals){
    intervals.forEach((interval) => {
      let perWidth = (interval._totalDuration / this._totalDuration) * 100
      interval.segmentElement.style.width = `${perWidth}%`
    })
  }

  renderNextIntervalPreview() {
    if (this.onLastInterval()){
      this.nextActionMessageTarget.innerText = ""
    } else {
      this.nextInterval().renderFirstActionPreview()
    }
  }

  clearPlayerMessages() {
    this.resetPresentation()
    this.clickMessageTarget.innerText = ""
    this.totalElapsedTimeMessageTarget.innerText = ""
    this.elapsedTimeMessageTarget.innerText = ""
    this.actionMessageTarget.innerText = ""
    if (this.hasNextActionMessageTarget) {
      this.nextActionMessageTarget.innerText = ""
    }
    this.hideAll(this.element.querySelectorAll(".interval-image"))
  }

  setupBrowser(){
    this.setAudioSession("transient")

    if (!this.isTouchEnabled() && this.hasSoundHelpTarget) {
      this.soundHelpTarget.remove()
    }

    this.listenToDocument("turbo:visit", () => {
      this.stop()
    })

    // noSleep se deshabilita cuando el usuario switchea la app,
    // así que lo re-habilito cuando vuelve (y hace click xq necesito que sea user-triggered)
    this.listenToDocument(
      'click',
      () => {
        if (this.noSleep && this.noSleep.isEnabled && this.stateValue === "playing") {
          this.noSleep.disable()
          this.noSleep = new NoSleep() // sin esto no me funcionaba
          this.noSleep.enable()
        }
      }
    );


    this.listenToDocument("visibilitychange", () => {
      if (this.stateValue === "playing" && document.visibilityState !== "visible") {
        // se va de la app pero el timer está corriendo!!
        if (this.isIos() && this.isInstalled()) {
          // en iOS, si está instalado, detengo el timer.
          this.stop()
          this.messageSW("timer-auto-stopped-ios")
        }
      }
    })
  }

  deSetupBrowser(){
    this.setAudioSession("playback")
  }

  // @see https://github.com/w3c/audio-session/blob/main/explainer.md
  setAudioSession(type) {
    if (navigator.audioSession) {
      this.log(`setupAudioSession: setting type to ${type}`)
      navigator.audioSession.type = type;
    }
  }

  setClickAudio(event){
    this.clickAudioTarget.src = event.target.value
    this.preloadAudios()
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.playAudio("click")
    }
  }

  setPhaseChangeAudio(event) {
    this.phaseChangeAudioTarget.src = event.target.value
    this.preloadAudios()
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.playAudio("phaseChange")
    }
  }

  setMetronomeChangeAudio(event) {
    this.metronomeChangeAudioTarget.src = event.target.value
    this.preloadAudios()
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.playAudio("metronomeChange")
    }
  }

  setEndAudio(event) {
    this.endAudioTarget.src = event.target.value
    this.preloadAudios()
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.playAudio("end")
    }
  }

  setStartAudio(event) {
    this.startAudioTarget.src = event.target.value
    this.preloadAudios()
    if (this.stateValue === "stopped" || this.stateValue === "finished") {
      this.playAudio("start")
    }
  }

  // enviar message al service worker
  messageSW(message){
    try {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message);
      } else {
        this.debug('no service worker controller');
      }
    } catch (e) {
      this.error('error messaging service worker', e);
    }
  }

  toggleNavigation(){
    this.log("toggleNavigation")
    switch(this.stateValue){
      case "loading":
      case "stopped":
      case "finished":
        this.log("showing nav")
        this.show(this.tapBar())
        this.show(this.footer())
        break
      case "pre-playing":
      case "playing":
        this.log("hiding nav")
        this.hide(this.tapBar())
        this.hide(this.footer())
        break
    }
  }

  tapBar(){
    return document.getElementById("tap-bar")
  }

  footer(){
    return document.querySelector("footer")
  }
}
